import { call } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import { Divider, Icon } from '@advanza/ui'
import { SUBSCRIPTION_STATUS_UNVERIFIED } from '@trustoo/types/entity/paymentInfo'
import { invalidateNotes } from 'actions/notes'
import { fetchProvider } from 'actions/providers'
import AdminUser from 'components/adminUsers/AdminUser'
import FreeLeadsButton from 'components/providers/FreeLeadsButton'
import FreeTrialButton from 'components/providers/FreeTrialButton'
import FreeTrialCancelButton from 'components/providers/FreeTrialCancelButton'
import FormattedTime from 'components/ui/FormattedTime'
import SafeButton from 'components/ui/SafeButton'
import { useAppDispatch } from 'hooks/hooks'
import { useProvider } from 'hooks/providerHooks'
import {
    FREE_LEAD_TYPE_FREE_TRIAL,
    FREE_LEAD_TYPE_PAUSE_RETENTION,
    FREE_LEAD_TYPE_SUBSCRIPTION_PROMO,
} from 'interfaces/FreeLeads'
import style from './freeLeads.module.scss'

interface Props {
    providerId: number
}

const FreeLeads = ({ providerId }: Props) => {
    const dispatch = useAppDispatch()

    const onRevoke = (id: number) => {
        return call('office/providers/revoke-free-leads/' + id, { method: 'post' }).then(() => {
            dispatch(fetchProvider(providerId, true))
            dispatch(invalidateNotes())
        })
    }

    const provider = useProvider(providerId).provider

    if (!provider?.free_leads) {
        return null
    }

    const activeFreeTrial = provider.active_free_trial

    return (
        <>
            {provider.free_leads.map(
                (
                    {
                        nr_leads,
                        nr_leads_left,
                        active,
                        free_lead_id,
                        user_id,
                        comment,
                        created,
                        expiration,
                        enable_after_nr_leads_payed,
                        free_lead_type,
                    },
                    i,
                ) => {
                    const isFreeTrial = free_lead_type === FREE_LEAD_TYPE_FREE_TRIAL
                    const isPauseRetention = free_lead_type === FREE_LEAD_TYPE_PAUSE_RETENTION
                    // Promo leads (one-time or recurring from promos): have free_lead_type = 'subscription_promo'
                    // or a known system-generated recurring promo comment prefix.
                    // Regular free leads (from "give free leads" modal): should not show expiry date
                    const isPromoLead =
                        free_lead_type === FREE_LEAD_TYPE_SUBSCRIPTION_PROMO ||
                        comment.startsWith('Auto Recurring Leads Promo')
                    // Only show expiry date for promo leads (one-time or recurring) that have an expiration date
                    const shouldShowExpiry = isPromoLead && !!expiration
                    // TRUS-8069 retention leads are intentionally non-expiring; surface that to admins explicitly.
                    const shouldShowNeverExpires = isPauseRetention && expiration === null

                    return (
                        <Row key={i} className={active ? '' : style.inActive} between="xs">
                            <Col xs>
                                <b>
                                    {nr_leads_left}/{nr_leads}
                                </b>{' '}
                                leads left from <AdminUser userId={user_id} />{' '}
                                <small>
                                    <FormattedTime format="yyyy-MM-dd" date={created} />{' '}
                                    {shouldShowExpiry && (
                                        <>
                                            expiry{' '}
                                            <FormattedTime
                                                format="yyyy-MM-dd"
                                                date={expiration}
                                            />{' '}
                                        </>
                                    )}
                                    {shouldShowNeverExpires && <>never expires </>}
                                    {comment}{' '}
                                </small>
                                {enable_after_nr_leads_payed ? (
                                    <>
                                        {' '}
                                        <small>
                                            [require {enable_after_nr_leads_payed} payed leads]
                                        </small>
                                    </>
                                ) : null}
                                {isFreeTrial && (
                                    <b style={{ color: '#0ea66b' }}>
                                        {' '}
                                        <Icon
                                            name="explosion"
                                            green
                                            fontSize={16}
                                            style={{
                                                verticalAlign: 'middle',
                                                paddingLeft: 3,
                                            }}
                                        />{' '}
                                        Free trial
                                    </b>
                                )}
                                {isPauseRetention && (
                                    <b style={{ color: '#2563eb' }}>
                                        {' '}
                                        <Icon
                                            name="schedule"
                                            fontSize={16}
                                            style={{
                                                color: '#2563eb',
                                                verticalAlign: 'middle',
                                                paddingLeft: 3,
                                            }}
                                        />{' '}
                                        Pause retention
                                    </b>
                                )}
                            </Col>
                            <Col x>
                                <SafeButton
                                    buttonText={<Icon name="undo" />}
                                    action={() => onRevoke(free_lead_id)}
                                    confirmMessage={
                                        isFreeTrial ? 'delete free trial' : 'delete free leads'
                                    }
                                    cancelText={false}
                                />
                            </Col>
                        </Row>
                    )
                },
            )}
            <Divider m />
            <FreeLeadsButton providerId={providerId} />
            {!provider.isSigned &&
                !provider.isCancelSigningDate &&
                !provider.isUnsubscribed &&
                provider.subscriptionStatus !== SUBSCRIPTION_STATUS_UNVERIFIED &&
                !provider.isFreeTrial && (
                    <>
                        <Divider m />
                        <FreeTrialButton providerId={providerId} />
                    </>
                )}
            {activeFreeTrial && (
                <>
                    <Divider m />
                    <FreeTrialCancelButton
                        onCancel={() => onRevoke(activeFreeTrial.free_lead_id)}
                    />
                </>
            )}
        </>
    )
}

export default FreeLeads
